import React, { useState, useEffect, cloneElement } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Grid,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Paper as MuiPaper,
  Avatar,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Event as EventIcon,
  Person as PersonIcon,
  DirectionsBoat as BoatIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Group as GroupIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  CheckCircleOutline as PaidIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getReservationById,
  updateReservation 
} from '../../firebase/services/reservationService';
import { formatPhoneNumber, formatTime12Hour } from '../../utils/formatters';
import { getEmailCorrespondenceForReservation } from '../../firebase/services/emailCorrespondenceService';
import { EmailCorrespondence, Reservation } from '../../models/types';
import { collection, getDocs, query, where, getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { formatInTimeZone } from 'date-fns-tz';

// Tab panel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reservation-tabpanel-${index}`}
      aria-labelledby={`reservation-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `reservation-tab-${index}`,
    'aria-controls': `reservation-tabpanel-${index}`,
  };
}

// Helper function for title case
function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}

const StaffReservationDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  
  // State for reservation data
  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // State for tabs
  const [tabValue, setTabValue] = useState(0);
  
  // State for correspondence
  const [correspondence, setCorrespondence] = useState<EmailCorrespondence[]>([]);
  const [correspondenceLoading, setCorrespondenceLoading] = useState(false);
  
  // State for email dialogs
  const [openResendDialog, setOpenResendDialog] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendingEmail, setResendingEmail] = useState(false);
  
  // State for email detail dialog
  const [emailDetailDialogOpen, setEmailDetailDialogOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailCorrespondence | null>(null);
  
  // Hardcoded location ID for Fallen Leaf Marina
  const FALLEN_LEAF_LOCATION_ID = 'mz9knr7tZGItuksXGvjM';

  useEffect(() => {
    const fetchReservation = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const reservationData = await getReservationById(id);
        
        if (!reservationData) {
          setError('Reservation not found');
          return;
        }
        
        setReservation(reservationData);
        
        // Pre-fetch correspondence data
        fetchCorrespondenceData(id);
      } catch (err) {
        console.error('Error fetching reservation:', err);
        setError('Failed to load reservation details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchReservation();
  }, [id]);

  // Function to handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Function to fetch correspondence data
  const fetchCorrespondenceData = async (reservationId: string) => {
    try {
      setCorrespondenceLoading(true);
      const correspondenceData = await getEmailCorrespondenceForReservation(reservationId);
      setCorrespondence(correspondenceData);
    } catch (error) {
      console.error('Error fetching correspondence data:', error);
    } finally {
      setCorrespondenceLoading(false);
    }
  };

  // Function to handle viewing full email content
  const handleViewFullEmail = (email: EmailCorrespondence) => {
    setSelectedEmail(email);
    setEmailDetailDialogOpen(true);
  };

  // Handler for resending confirmation email
  const handleResendConfirmation = async () => {
    if (!reservation) return;
    
    // Set the default email to the customer's email
    setResendEmail(reservation.customerDetails.email);
    setOpenResendDialog(true);
  };

  const handleConfirmResend = async () => {
    try {
      setResendingEmail(true);
      console.log('Resending email for reservation:', reservation);
      
      // Get the email templates for this location
      const locationId = reservation?.locationId || FALLEN_LEAF_LOCATION_ID;
      const templatesRef = collection(db, `locations/${locationId}/transactionalEmailTemplates`);
      const q = query(templatesRef, where('type', '==', 'reservation_confirmation'));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        throw new Error('No reservation confirmation templates found');
      }
      
      const templates = querySnapshot.docs.map(doc => doc.data());
      // Find default template
      const template = templates.find(t => t.isDefault) || templates[0];
      
      if (!template) {
        throw new Error('No default reservation confirmation template found');
      }

      // Get location data directly from Firestore
      const locationDoc = await getDoc(doc(db, 'locations', locationId));
      
      if (!locationDoc.exists()) {
        throw new Error(`Location not found with ID: ${locationId}`);
      }
      
      const locationData = locationDoc.data();

      // Update template content to use underscore notation
      let updatedBody = template.body;
      updatedBody = updatedBody.replace(/{{customerDetails\.firstName}}/g, '{{customerDetails_firstName}}');
      updatedBody = updatedBody.replace(/{{payment\.depositAmount}}/g, '{{payment_depositAmount}}');
      updatedBody = updatedBody.replace(/{{payment\.remainingAmount}}/g, '{{payment_remainingAmount}}');
      
      const subject = template.subject;
      const body = updatedBody;

      // Format vessel type and currency
      const formatVesselType = (type: string) => {
        return type.split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
      };

      const formatCurrency = (value?: number) => {
        if (value === undefined) return '';
        return value.toFixed(2);
      };

              // Prepare all variables
        const variables = {
          id: reservation?.id,
          customerDetails_firstName: reservation?.customerDetails.firstName,
          customerDetails_lastName: reservation?.customerDetails.lastName,
          customerDetails_email: reservation?.customerDetails.email,
          customerDetails_phone: formatPhoneNumber(reservation?.customerDetails.phone || ''),
          vesselType: formatVesselType(reservation?.vesselType || ''),
          vesselName: reservation?.vesselName,
          date: format(parseISO(reservation?.date || ''), 'MMMM d, yyyy'),
          startDate: formatInTimeZone(reservation?.date || '', 'America/Los_Angeles', 'M/d/yyyy'),
          startTime: formatTime12Hour(reservation?.startTime || ''),
          endTime: formatTime12Hour(reservation?.endTime || ''),
          partySize: reservation?.partySize,
          originalPrice: formatCurrency(reservation?.originalPrice),
          finalPrice: formatCurrency(reservation?.finalPrice),
          damageWaiverAmount: formatCurrency(reservation?.damageWaiverAmount),
          taxAmount: formatCurrency(reservation?.taxAmount),
          discountAmount: formatCurrency(reservation?.discountAmount),
          status: reservation?.status,
          promoCode: reservation?.promoCode || '',
          marinaName: locationData.name,
          marinaPhone: formatPhoneNumber(locationData.phone),
          marinaEmail: locationData.email,
          marinaAddress: locationData.address,
          marinaWebsite: locationData.website,
          payment_depositAmount: Number(reservation?.payment?.depositAmount || 0).toFixed(2),
          payment_remainingAmount: Number(reservation?.payment?.remainingAmount || 0).toFixed(2),
          paymentDetails: {
            cardBrand: reservation?.payment?.paymentDetails?.cardBrand || '',
            last4: reservation?.payment?.paymentDetails?.last4 || '',
          }
        };

      // Call Firebase Function to send email
      const sendEmailFunction = httpsCallable(functions, 'sendEmail');
      await sendEmailFunction({
        to: resendEmail,
        subject,
        body,
        templateId: template.id,
        reservationId: reservation?.id,
        ...variables
      });

      alert('Confirmation email resent successfully');
      setOpenResendDialog(false);
      
      // Refresh correspondence data
      setTimeout(() => {
        fetchCorrespondenceData(reservation?.id || '');
      }, 1500);
    } catch (error) {
      console.error('Error resending confirmation email:', error);
      alert('Failed to resend confirmation email');
    } finally {
      setResendingEmail(false);
    }
  };

  // Format date function for correspondence
  const formatDate = (date: Date | undefined) => {
    if (!date) return 'N/A';
    return format(date, 'MMM d, yyyy h:mm a');
  };

  // Helper function to get correct payment status
  const getPaymentStatus = () => {
    if (!reservation?.payment) {
      return { status: 'Unpaid', color: 'error' as const, icon: <WarningIcon /> };
    }

    const { depositAmount = 0, remainingAmount = 0 } = reservation.payment;
    
    if (remainingAmount <= 0) {
      return { status: 'Paid', color: 'success' as const, icon: <PaidIcon /> };
    } else if (depositAmount > 0) {
      return { status: 'Partially Paid', color: 'warning' as const, icon: <MoneyIcon /> };
    } else {
      return { status: 'Unpaid', color: 'error' as const, icon: <WarningIcon /> };
    }
  };

  // Helper function to calculate payment progress
  const getPaymentProgress = () => {
    if (!reservation?.finalPrice || !reservation?.payment?.depositAmount) return 0;
    return (reservation.payment.depositAmount / reservation.finalPrice) * 100;
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !reservation) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || 'Reservation not found'}
        </Alert>
        <Button
          onClick={() => navigate('/staff/reservations')}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Reservations
        </Button>
      </Container>
    );
  }

  const paymentStatus = getPaymentStatus();
  const paymentProgress = getPaymentProgress();

  return (
    <>
    <Container maxWidth="lg">
      <Box sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '200px',
        borderRadius: '16px 16px 0 0',
        position: 'relative',
        mb: 3,
        overflow: 'hidden'
      }}>
        {/* Background pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start', 
            pt: 4, 
            pb: 2,
            color: 'white'
          }}>
            <Box>
              <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'white' }}>
                Reservation Details
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  #{reservation.id}
                </Typography>
                <Chip 
                  icon={<EventIcon />}
                  label={toTitleCase(reservation.status)}
                  color={
                    reservation.status === 'confirmed' ? 'success' : 
                    reservation.status === 'pending' ? 'warning' : 
                    reservation.status === 'cancelled' ? 'error' :
                    'default'
                  }
                  size="medium"
                  sx={{ 
                    fontWeight: 600,
                    '& .MuiChip-icon': { fontSize: '1.1rem' }
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                onClick={() => navigate(`/staff/reservations/${reservation.id}/edit`)}
                startIcon={<EditIcon />}
                variant="contained"
                sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.2)', 
                  backdropFilter: 'blur(10px)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
                }}
              >
                Edit
              </Button>
              <Button
                onClick={() => navigate('/staff/reservations')}
                startIcon={<ArrowBackIcon />}
                variant="outlined"
                sx={{ 
                  color: 'white', 
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
              >
                Back to Reservations
              </Button>
            </Box>
          </Box>

          {/* Quick Stats Row */}
          <Grid container spacing={2} sx={{ pb: 4 }}>
            <Grid item xs={12} sm={3}>
              <Box sx={{ 
                backgroundColor: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(10px)',
                borderRadius: 2, 
                p: 2, 
                textAlign: 'center',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <BoatIcon sx={{ fontSize: 32, color: 'white', mb: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  {toTitleCase(reservation.vesselType)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Box sx={{ 
                backgroundColor: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(10px)',
                borderRadius: 2, 
                p: 2, 
                textAlign: 'center',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <GroupIcon sx={{ fontSize: 32, color: 'white', mb: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  {reservation.partySize} People
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Box sx={{ 
                backgroundColor: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(10px)',
                borderRadius: 2, 
                p: 2, 
                textAlign: 'center',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <MoneyIcon sx={{ fontSize: 32, color: 'white', mb: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  ${reservation.finalPrice?.toFixed(2)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Box sx={{ 
                backgroundColor: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(10px)',
                borderRadius: 2, 
                p: 2, 
                textAlign: 'center',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                {cloneElement(paymentStatus.icon, { 
                  sx: { fontSize: 32, color: 'white', mb: 1 } 
                })}
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  {paymentStatus.status}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Paper elevation={3} sx={{ borderRadius: '0 0 16px 16px', overflow: 'hidden' }}>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="reservation details tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                minHeight: 48,
                '&.Mui-selected': {
                  color: '#667eea'
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#667eea',
                height: 3,
                borderRadius: '3px 3px 0 0'
              }
            }}
          >
            <Tab label="Details" {...a11yProps(0)} />
            <Tab label="Email History" {...a11yProps(1)} />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
            {reservation.notes && (
                <Grid item xs={12}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                    borderRadius: 3,
                    overflow: 'hidden'
                  }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
                        📝 Notes
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.6 }}>
                        {reservation.notes}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Card sx={{ 
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white',
                  borderRadius: 3,
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <Box sx={{
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }} />
                  <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', mr: 2 }}>
                        <PersonIcon />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Customer Information
                      </Typography>
                    </Box>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                      {reservation.customerDetails.firstName} {reservation.customerDetails.lastName}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9, mb: 1 }}>
                      📧 {reservation.customerDetails.email}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      📱 {formatPhoneNumber(reservation.customerDetails.phone)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card sx={{ 
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  color: 'white',
                  borderRadius: 3,
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <Box sx={{
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }} />
                  <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', mr: 2 }}>
                        <CalendarIcon />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Reservation Details
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CalendarIcon sx={{ mr: 1, fontSize: 20 }} />
                      <Typography variant="body1">
                        {format(parseISO(reservation.date), 'EEEE, MMMM d, yyyy')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <TimeIcon sx={{ mr: 1, fontSize: 20 }} />
                      <Typography variant="body1">
                        {formatTime12Hour(reservation.startTime)} - {formatTime12Hour(reservation.endTime)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BoatIcon sx={{ mr: 1, fontSize: 20 }} />
                      <Typography variant="body1">
                        {toTitleCase(reservation.vesselType)} • {reservation.partySize} people
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card sx={{ 
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  borderRadius: 3,
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <Box sx={{
                    position: 'absolute',
                    top: -30,
                    right: -30,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }} />
                  <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', mr: 2 }}>
                        <MoneyIcon />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                        Payment Information
                      </Typography>
                    </Box>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ 
                          backgroundColor: 'rgba(255,255,255,0.2)', 
                          borderRadius: 2, 
                          p: 2, 
                          textAlign: 'center',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                            Total Amount
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                            ${reservation.finalPrice?.toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ 
                          backgroundColor: 'rgba(255,255,255,0.2)', 
                          borderRadius: 2, 
                          p: 2, 
                          textAlign: 'center',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                            Payment Status
                          </Typography>
                          <Chip 
                            icon={paymentStatus.icon}
                            label={paymentStatus.status}
                            color={paymentStatus.color}
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                      </Grid>
                      
                      {reservation.payment?.depositAmount && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ 
                            backgroundColor: 'rgba(255,255,255,0.2)', 
                            borderRadius: 2, 
                            p: 2, 
                            textAlign: 'center',
                            backdropFilter: 'blur(10px)'
                          }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                              Deposit Paid
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                              ${reservation.payment.depositAmount.toFixed(2)}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      
                      {reservation.payment?.remainingAmount && reservation.payment.remainingAmount > 0 && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ 
                            backgroundColor: 'rgba(255,255,255,0.2)', 
                            borderRadius: 2, 
                            p: 2, 
                            textAlign: 'center',
                            backdropFilter: 'blur(10px)'
                          }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                              Balance Due
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                              ${reservation.payment.remainingAmount.toFixed(2)}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>

                    {/* Payment Progress Bar */}
                    {paymentProgress > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                            Payment Progress
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                            {Math.round(paymentProgress)}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={paymentProgress} 
                          sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            backgroundColor: 'rgba(255,255,255,0.3)',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: 'white'
                            }
                          }} 
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 4,
              p: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 3,
              color: 'white'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', mr: 2 }}>
                  <EmailIcon />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Email Correspondence
                </Typography>
              </Box>
              <Button
                onClick={handleResendConfirmation}
                startIcon={<EmailIcon />}
                variant="contained"
                disabled={resendingEmail}
                sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.2)', 
                  backdropFilter: 'blur(10px)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
                }}
              >
                {resendingEmail ? 'Sending...' : 'Resend Confirmation'}
              </Button>
            </Box>
            
            {correspondenceLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : correspondence.length > 0 ? (
              <Grid container spacing={2}>
                {correspondence.map((email, index) => (
                  <Grid item xs={12} key={index}>
                    <Card sx={{ 
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      color: 'white',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <Box sx={{
                        position: 'absolute',
                        top: -20,
                        right: -20,
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }} />
                      <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                              {email.subject}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                              📧 To: {email.recipient}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                              🕒 {formatDate(email.timestamp)}
                            </Typography>
                          </Box>
                          <Button
                            onClick={() => handleViewFullEmail(email)}
                            startIcon={<EmailIcon />}
                            variant="contained"
                            size="small"
                            sx={{ 
                              backgroundColor: 'rgba(255,255,255,0.2)', 
                              backdropFilter: 'blur(10px)',
                              '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
                            }}
                          >
                            View Full Email
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Card sx={{ 
                borderRadius: 3,
                background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                textAlign: 'center',
                py: 6
              }}>
                <CardContent>
                  <EmailIcon sx={{ fontSize: 64, color: '#666', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Email Correspondence Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No emails have been sent for this reservation yet.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        </TabPanel>
      </Paper>
    </Container>

    {/* Email Resend Dialog */}
    <Dialog
      open={openResendDialog}
      onClose={() => setOpenResendDialog(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Resend Confirmation Email</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Enter the email address to send the confirmation to:
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          label="Email Address"
          type="email"
          fullWidth
          variant="outlined"
          value={resendEmail}
          onChange={(e) => setResendEmail(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenResendDialog(false)}>Cancel</Button>
        <Button 
          onClick={handleConfirmResend} 
          color="primary" 
          variant="contained"
          disabled={resendingEmail}
        >
          {resendingEmail ? 'Sending...' : 'Send Email'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Email Detail Dialog */}
    <Dialog
      open={emailDetailDialogOpen}
      onClose={() => setEmailDetailDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Email Details</DialogTitle>
      <DialogContent>
        {selectedEmail && (
          <>
            <Typography variant="subtitle2" gutterBottom>Subject:</Typography>
            <Typography variant="body2" gutterBottom>{selectedEmail.subject}</Typography>
            
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>To:</Typography>
            <Typography variant="body2" gutterBottom>{selectedEmail.recipient}</Typography>
            
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Sent:</Typography>
            <Typography variant="body2" gutterBottom>{formatDate(selectedEmail.timestamp)}</Typography>
            
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Content:</Typography>
            <MuiPaper sx={{ p: 2, backgroundColor: 'grey.50' }}>
              <div dangerouslySetInnerHTML={{ __html: selectedEmail.body }} />
            </MuiPaper>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEmailDetailDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default StaffReservationDetailsPage; 